import { fillProfileUrl } from '@utils/urlUtils';
import { PublicCertification } from '@wix/ambassador-academy-certifications-v1-account-certification/types';
import {
  Box,
  Button,
  Divider,
  Heading,
  Image,
  Text,
  Tooltip,
} from '@wix/design-system';
import { UploadExport } from '@wix/wix-ui-icons-common';
import clsx from 'clsx';
import React, { FC, Suspense, useMemo } from 'react';
import { useProfileContext } from '../../Contexts/ProfileContext';
import Certifications from '../Certifications';
import { shouldRenderCertifications } from '../Certifications/CertificationsUtils';
import ProviderReviewSummary from '../ProviderReviewSummary';
import Services from '../Services/Services';
import ContactButton from './ContactButton';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { useScheduleMeetingButton } from '@components/Hooks/ExperimentHooks/useScheduleMeetingButton';
import s from './ContactPanel.module.scss';

type Props = {
  isMobileView: boolean;
  onContactClicked: () => void;
  copy?: (value: string) => Promise<void>;
  onCopyClicked: () => void;
  onScheduleMeetingClicked: () => void;
  shareUrl: string;
  certifications: PublicCertification[];
  showProviderReviewSummary?: boolean;
};

const ContactPanel: FC<Props> = ({
  isMobileView,
  onContactClicked,
  copy,
  onCopyClicked = () => {},
  onScheduleMeetingClicked,
  shareUrl,
  certifications,
  showProviderReviewSummary = true,
}) => {
  const { t } = useTranslation();
  const { partnerProfile } = useProfileContext();
  const { isScheduleMeetingButtonEnabled } = useScheduleMeetingButton();

  const hasServices = useMemo(
    () => (partnerProfile.services?.values?.length || 0) > 0,
    [partnerProfile.services],
  );

  const hasCertifications = useMemo(
    () => shouldRenderCertifications(certifications),
    [certifications],
  );

  return (
    <Box
      direction="vertical"
      dataHook="profile-contact-panel"
      className={clsx(
        !isMobileView && s.fixedLeftbar,
        s.container,
        isMobileView && s.mobile,
      )}
      width={isMobileView ? '100%' : 279}
    >
      <Box
        direction="vertical"
        className={clsx(
          s.contentContainer,
          s.sectionPadding,
          isMobileView && s.mobile,
        )}
      >
        <div className={s.profileImageContainer}>
          <Image
            alt={partnerProfile?.professionalInformation?.businessName || ''}
            borderRadius="100px"
            transparent
            width="90px"
            height="90px"
            src={fillProfileUrl(
              partnerProfile?.professionalInformation?.profileImage || '',
              190,
              190,
            )}
          />
        </div>
        <Box
          className={clsx(s.businessNameContainer, isMobileView && s.mobile)}
        >
          <Heading
            size="large"
            as="h1"
            ellipsis
            maxLines={2}
            dataHook="contact-panel-business-name"
          >
            {partnerProfile.professionalInformation?.businessName}
          </Heading>
        </Box>

        {isMobileView && showProviderReviewSummary && (
          <Box width="100%">
            <ProviderReviewSummary
              isStickyMobileView={false}
              isMobileView={isMobileView}
            />
          </Box>
        )}

        <Box
          paddingTop="6px"
          width="100%"
          className={clsx(s.tagline, isMobileView && s.mobile)}
        >
          <Text
            size={isMobileView ? 'medium' : 'small'}
            weight={'thin'}
            ellipsis
            maxLines={2}
            secondary={isMobileView}
            width="100%"
          >
            {partnerProfile.professionalInformation?.tagline}
          </Text>
        </Box>

        {!isMobileView && showProviderReviewSummary && (
          <ProviderReviewSummary
            isStickyMobileView={false}
            isMobileView={isMobileView}
            containerClassName={s.desktopContactPanel}
          />
        )}

        <Box
          direction="vertical"
          paddingTop={isMobileView ? '12px' : '24px'}
          paddingBottom={isMobileView ? '30px' : '0'}
          gap="12px"
          align={isMobileView ? 'center' : 'left'}
        >
          <Box gap={isMobileView ? '12px' : '6px'}>
            <ContactButton onContactClicked={onContactClicked} />
            <Tooltip content={t('marketplace-profile-page-share-link-tooltip')}>
              <Button
                className={s.shareButton}
                dataHook="profile-contact-panel-share"
                priority="secondary"
                skin="dark"
                size="medium"
                onClick={() => {
                  copy?.(shareUrl);
                  onCopyClicked();
                }}
              >
                <UploadExport />
              </Button>
            </Tooltip>
          </Box>

          {isScheduleMeetingButtonEnabled &&
            partnerProfile.scheduling?.link && (
              <Button
                dataHook="profile-contact-panel-schedule-meeting"
                priority="secondary"
                skin="dark"
                size="medium"
                onClick={() => {
                  onScheduleMeetingClicked();
                  window.open(
                    partnerProfile.scheduling!.link!,
                    '_blank',
                    'noopener,noreferrer',
                  );
                }}
              >
                {t('marketplace-profile-page-schedule-meeting')}
              </Button>
            )}
        </Box>
      </Box>

      {!isMobileView && (
        <>
          {hasServices && <Divider className={s.divider} />}

          <Box className={clsx(s.sectionPadding, isMobileView && s.mobile)}>
            <Services />
          </Box>

          {hasCertifications && <Divider className={s.divider} />}

          <Box className={clsx(s.sectionPadding, isMobileView && s.mobile)}>
            <Suspense fallback="">
              <Certifications
                isMobileView={isMobileView}
                variant="compact"
                data={certifications}
              />
            </Suspense>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ContactPanel;
